import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchMessages, sendMessage, pingTyping } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";
import AccountSidebar from "@/components/AccountSidebar";
import { PresenceDot } from "@/components/PresenceDot";
import { Send, ChevronLeft, Check, CheckCheck, ArrowDown } from "lucide-react";
import { withWatermark } from "@/lib/utils";

// Polls fast (2s) when tab visible, slow (10s) when hidden.
function useAdaptivePoll(callback, deps) {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const loop = async () => {
      if (cancelled) return;
      try {
        await cbRef.current();
      } catch (e) {
        logger.warn("Conversation poll failed:", e?.message || e);
      }
      if (cancelled) return;
      const delay = document.visibilityState === "visible" ? 2000 : 10000;
      timer = setTimeout(loop, delay);
    };
    loop();
    const onVis = () => {
      if (document.visibilityState === "visible" && !cancelled) {
        if (timer) clearTimeout(timer);
        loop();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // The `deps` array is passed by the caller and applied as-is to control the
    // polling loop's lifetime. ESLint can't statically verify this pattern but
    // it's intentional — see the custom-hook contract above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yest.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function ConversationPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState({ conversation: null, messages: [], typing: false });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const endRef = useRef(null);
  const scrollerRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const lastTypingPingRef = useRef(0);

  const isAtBottom = () => {
    const el = scrollerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const refresh = useCallback(async () => {
    const next = await fetchMessages(id);
    setData((prev) => {
      const prevLastId = prev.messages.length ? prev.messages[prev.messages.length - 1].id : null;
      const newLastId = next.messages.length ? next.messages[next.messages.length - 1].id : null;
      if (prevLastId && newLastId && prevLastId !== newLastId) {
        const last = next.messages[next.messages.length - 1];
        const mine = last.sender_id === user?.user_id;
        if (!mine && !isAtBottom()) setHasNew(true);
        // Server-side Web Push handles notifications via Service Worker;
        // no in-tab notification needed here.
      }
      return next;
    });
  }, [id, user?.user_id]);

  useAdaptivePoll(refresh, [id]);

  // Initial fetch + scroll on mount
  useEffect(() => {
    refresh();
  }, [id, refresh]);

  // Ask for notification permission once
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);  // Auto-scroll when new message arrives and we're at bottom
  useEffect(() => {
    const lastId = data.messages.length ? data.messages[data.messages.length - 1].id : null;
    if (lastId !== lastMsgIdRef.current) {
      lastMsgIdRef.current = lastId;
      if (isAtBottom()) {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
        setHasNew(false);
      }
    }
  }, [data.messages]);

  const onScroll = () => {
    if (isAtBottom()) setHasNew(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) return;
    setSending(true);
    // Optimistic message
    const optimistic = {
      id: `tmp-${Date.now()}`,
      conversation_id: id,
      sender_id: user?.user_id,
      sender_name: user?.name || "Vous",
      text: v,
      created_at: new Date().toISOString(),
      read_at: null,
      _optimistic: true,
    };
    setData((prev) => ({ ...prev, messages: [...prev.messages, optimistic] }));
    setText("");
    // Force scroll to bottom on send
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    try {
      await sendMessage(id, v);
      await refresh();
    } catch {
      setData((prev) => ({ ...prev, messages: prev.messages.filter((m) => m.id !== optimistic.id) }));
    } finally {
      setSending(false);
    }
  };

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (now - lastTypingPingRef.current > 2500) {
      lastTypingPingRef.current = now;
      pingTyping(id).catch(() => {});
    }
  };

  // Group messages by day
  const grouped = [];
  let lastDay = null;
  for (const m of data.messages) {
    const day = m.created_at?.slice(0, 10);
    if (day !== lastDay) {
      grouped.push({ kind: "day", id: `d-${day}`, label: dayLabel(m.created_at) });
      lastDay = day;
    }
    grouped.push({ kind: "msg", ...m });
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="conversation-page">
      <Link to="/messages" className="inline-flex items-center gap-1 text-orange-600 font-semibold mb-5">
        <ChevronLeft size={16} /> Retour
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div className="hidden lg:block"><AccountSidebar /></div>
        <section className="glass-card flex flex-col h-[75vh] relative overflow-hidden">
          {/* Header */}
          <div className="border-b border-[var(--line)] p-4 flex items-center gap-3 bg-white/70 backdrop-blur">
            {data.conversation?.annonce_image && (
              <img src={withWatermark(data.conversation.annonce_image)} className="w-12 h-12 rounded-xl object-cover" alt="" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate flex items-center gap-2" data-testid="conv-title">
                <span className="truncate">{data.conversation?.annonce_title || "Conversation"}</span>
                {(() => {
                  const otherId = (data.conversation?.participants || []).find((p) => p !== user?.user_id);
                  return otherId ? <PresenceDot userId={otherId} /> : null;
                })()}
              </div>
              <div className="text-xs text-[var(--ink-soft)] truncate">
                {data.typing ? (
                  <span className="text-orange-600 font-semibold inline-flex items-center gap-1" data-testid="typing-indicator">
                    en train d&apos;ecrire
                    <TypingDots />
                  </span>
                ) : (
                  <>Annonce #{data.conversation?.annonce_id}</>
                )}
              </div>
            </div>
            {data.conversation?.annonce_id && (
              <Link to={`/annonces/${data.conversation.annonce_id}`} className="btn-secondary !text-xs">Voir l&apos;annonce</Link>
            )}
          </div>

          {/* Messages */}
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="flex-1 overflow-y-auto p-4 space-y-2 bg-gradient-to-b from-orange-50/30 to-transparent"
            data-testid="conv-messages"
          >
            {grouped.map((item) => {
              if (item.kind === "day") {
                return (
                  <div key={item.id} className="flex justify-center my-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)] bg-white/80 px-3 py-1 rounded-full border border-[var(--line)]">
                      {item.label}
                    </span>
                  </div>
                );
              }
              const m = item;
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm shadow-sm ${
                    mine
                      ? "bg-gradient-to-br from-orange-500 to-orange-400 text-white rounded-br-md"
                      : "bg-white border border-[var(--line)] rounded-bl-md"
                  } ${m._optimistic ? "opacity-70" : ""}`}>
                    {!mine && (
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)] mb-0.5">
                        {m.sender_name}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "text-white/80 justify-end" : "text-[var(--ink-soft)]"}`}>
                      <span>{fmtTime(m.created_at)}</span>
                      {mine && !m._optimistic && (
                        m.read_at
                          ? <CheckCheck size={12} className="text-white" data-testid={`msg-read-${m.id}`} />
                          : <Check size={12} className="text-white/70" data-testid={`msg-sent-${m.id}`} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {data.typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--line)] rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
                  <TypingDots />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* "New messages" floating button */}
          {hasNew && (
            <button
              onClick={() => {
                endRef.current?.scrollIntoView({ behavior: "smooth" });
                setHasNew(false);
              }}
              className="absolute bottom-20 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg hover:bg-orange-600"
              data-testid="new-messages-btn"
            >
              <ArrowDown size={12} /> Nouveaux messages
            </button>
          )}

          {/* Composer */}
          <form onSubmit={submit} className="border-t border-[var(--line)] p-3 flex gap-2 bg-white" data-testid="conv-send-form">
            <input
              value={text}
              onChange={onType}
              placeholder="Tapez votre message..."
              data-testid="conv-input"
              className="flex-1 px-4 py-2.5 rounded-full border border-[var(--line)] focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!text.trim() || sending}
              className="btn-primary inline-flex items-center gap-2 !rounded-full"
              data-testid="conv-send-btn"
            >
              <Send size={14} /> <span className="hidden sm:inline">{sending ? "..." : "Envoyer"}</span>
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-end gap-0.5 ml-0.5">
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="w-1 h-1 bg-current rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
    </span>
  );
}
